import { useEffect, useState } from 'react';
import { calculatPrice } from '../core/price';

const createFontFamily = (name: string, ttf: string, weight: string = 'normal') => `
@font-face {
  font-family: '${name}';
  font-style: 'normal';
  font-weight: '${weight}';
  src: url('https://neon-static.s3.amazonaws.com/fonts/${ttf}');
}
`;
const colors = [
  { name: 'Hot Mama Red', color: '#ff4f4f', example: 'red' },
  { name: 'Love Potion Pink', color: '#fc35d0', example: 'lovepotion' },
  { name: 'Pastel Pink', color: '#ff82e3', example: 'pink' },
  { name: 'Hopeless Romantic Purple', color: '#b858f5 ', example: 'purple' },
  { name: 'Glacier Blue', color: '#69e8fc', example: 'glacier' },
  { name: 'Santorini Blue', color: '#6677ff', example: 'blue' },
  { name: 'Glow Up Green', color: '#34de00', example: 'green' },
  { name: 'Hey Pumpkin Orange', color: '#ffc424', example: 'pumpkin' },
  { name: 'Paradise Yellow', color: '#fbff57', example: 'yellow' },
  { name: 'Cosy Warm White', color: '#eeebd8', example: 'warmwhite' },
  { name: 'Snow White', color: '#ffffff', example: 'snow' },
];

const neons = [
  {
    title: 'Classic',
    value: 'THIN',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, neon: 'THIN' }, priceResolver),
    style: { fontWeight: 'lighter' },
    disabled: [],
    example: 'classic',
  },
  {
    title: 'Bold',
    value: 'MEDIUM',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, neon: 'MEDIUM' }, priceResolver),
    style: { fontWeight: 'normal' },
    disabled: ['MINI'],
    example: 'bold',
  },
  {
    title: 'Extra Bold',
    value: 'BOLD',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, neon: 'BOLD' }, priceResolver),
    style: { fontWeight: 'bold' },
    disabled: ['MINI', 'SMALL'],
    example: 'extrabold',
  },
];

const extras = [
  { title: 'Indoor only', description: 'For indoor use only', value: 'INDOOR', price: 0 },
  { title: 'Outdoor use (Splashproof)', description: 'For indoor and outdoor use', value: 'OUTDOOR', price: 75 },
];
const supports = [
  // { title: 'Hollow-out', description: 'The most discreet transparent backing', value: 'HOLLOW' },
  {
    title: 'Cut to shape',
    description: 'Acrylic display that follows the shape of your sign.',
    value: 'SHAPE',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, support: 'SHAPE' }, priceResolver),
    example: 'shape',
  },
  {
    title: 'Cut to Letter',
    description: 'Acrylic display that follows the letter of your design.',
    value: 'LETTER',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, support: 'LETTER' }, priceResolver),
    example: 'letter',
  },
  {
    title: 'Full board',
    description: 'Acrylic display in shape of a square around your sign',
    value: 'FULL',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, support: 'FULL' }, priceResolver),
    example: 'wholeboard',
  },
  // { title: 'Stand', description: 'Make your sign upright on the floor', value: 'STAND' },
];
const remote = [
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
];
const mounting = [
  { label: 'Yes', value: 'YES' },
  { label: 'No', value: 'NO' },
];
const powerSupllies = [
  {
    title: 'Wall Plug',
    value: 'WALLPLUG',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, power: 'WALLPLUG' }, priceResolver),
  },
  {
    title: 'Wall Plug + Battery Pack',
    description: 'Batteries not included',
    value: 'BATTERIES',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, { ...config, power: 'BATTERIES' }, priceResolver),
  },
];
const backgrounds = [
  { title: 'Default', value: 'DEFAULT', banner: '/images/empty.png' },
  { title: 'White wall', value: 'WALL-WHITE', banner: '/images/background.jpg' },
  {
    title: 'Keep calm',
    value: 'KEEP-CALM',
    banner: 'https://i.pinimg.com/originals/88/e2/08/88e2085713eb81bfecfefcdd3e9b05c8.jpg',
    price: 29.99,
    contain: true,
    background: 'rgb(210, 42, 42)',
    width: 500,
    height: 704,
    regio: {
      y1: 0.38,
      x1: 0.14,
      y2: 0.81,
      x2: 0.86,
      w: 37,
      h: 25,
    },
  },
];

const sizes = [
  {
    name: 'Small',
    value: 'MINI',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice('MINI', value, config, priceResolver),
    description: '10.5cm x 7cm',
    dimensions: [6, 9],
  },
  {
    name: 'Medium',
    value: 'SMALL',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice('SMALL', value, config, priceResolver),
    description: '12cm x 8cm',
    dimensions: [8, 12],
  },
  {
    name: 'Large',
    value: 'MEDIUM',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice('MEDIUM', value, config, priceResolver),
    description: '18cm x 12cm',
    dimensions: [12, 18],
  },
  {
    name: 'XL',
    value: 'LARGE',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice('LARGE', value, config, priceResolver),
    description: '24cm x 16cm',
    dimensions: [16, 24],
  },
  {
    name: 'XXL',
    value: 'GIANT',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice('GIANT', value, config, priceResolver),
    description: '20cm x 30cm',
    dimensions: [20, 30],
  },
  {
    name: 'Custom',
    value: 'Custom',
    price: (size: string, value: any, config: any, priceResolver: any) =>
      calculatPrice(size, value, config, priceResolver),
  },
];

const useParams = () => {
  const location = window.location;
  const params = new URLSearchParams(location.search.replace('?', ''));
  let options: null | { [key: string]: string | string[] } = null;
  // @ts-ignore
  for (var pair of params.entries()) {
    if (!options) options = {};
    options[pair[0]] = pair[1].includes('') ? pair[1].split(',') : pair[1];
  }

  return options;
};

const useFamilies = () => {
  const [families, setFamilies] = useState<{ name: string; family: string }[]>([]);
  const _handleFetch = async () => {
    const res = await fetch('https://neon-static.s3.amazonaws.com/fonts/fonts.txt').then((res) => res.text());

    const families = res.split('\n').map((line) => {
      const [name, ttf] = line.split(':');
      const markup = [
        createFontFamily(name, ttf),
        createFontFamily(name, ttf, '500 900'),
        createFontFamily(name, ttf, '100 300'),
      ].join('\n');
      let style = document.createElement('style');
      style.setAttribute('type', 'text/css');
      style.innerHTML = markup;

      document.head.appendChild(style);

      return { name, family: ttf };
    });
    setFamilies(families);
  };

  useEffect(() => {
    _handleFetch();
  }, []);
  //{ name: 'Poppins', family: 'Poppins' },

  return families;
};

export const useConfig = () => {
  const families = useFamilies();
  const config = useParams();

  let priceResolver: null | ((size: string) => number) = null;

  const options: { [key: string]: any[] } = {
    powerSupllies,
    colors,
    neons,
    extras,
    supports,
    remote,
    mounting,
    backgrounds,
    sizes,
  };

  if (!config?.product) options.families = families;
  if (config?.sizes) {
    const sizeOptions = (config.sizes as string[]).map((size) => size.split('_'));
    const sizeKeys = sizeOptions.map((size) => size[0].toUpperCase());
    const filteredSizes = options.sizes.filter((size) => sizeKeys.includes(size.value.toUpperCase()));
    const sizes = filteredSizes.map((size) => {
      const dimensions = sizeOptions
        .find((option) => option[0].toUpperCase() === size.value.toUpperCase())?.[1]
        ?.split('@')[1]
        ?.split('x');

      return {
        ...size,
        dimensions: dimensions ?? size.dimensions,
        exact: true,
        price: parseFloat(
          (sizeOptions.find((option) => option[0].toUpperCase() === size.value.toUpperCase())?.[1] ?? '0').split(
            '@',
          )[0],
        ),
      };
    });

    options.sizes = sizes;

    priceResolver = (size: string) => {
      const val = parseFloat(sizeOptions.find((_) => _[0].toUpperCase() === size.toUpperCase())?.[1] ?? '0');
      return val;
    };
  }

  return {
    options,
    priceResolver,
    banner: config?.banner,
    isPredefined: !!config?.product,
  };
};
